import { memo, useMemo } from 'react';
import { format } from 'date-fns';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { POPULARITY_WINDOW_LABELS, computeSeriesStats, formatPopularity } from '../../utils/starchart';
import { POPULARITY_WINDOWS, type PopularityWindow, type RankHistoryResponse } from '../../types/starchart';

interface PopularitySummaryCardProps {
  /** Current index per window, straight from the live rankings. */
  current: Partial<Record<PopularityWindow, number>>;
  currentRank: Partial<Record<PopularityWindow, number>>;
  /** ARGUS-collected history. Absent until the backend has been collecting. */
  rankHistory: RankHistoryResponse | null;
  window: PopularityWindow;
}

const Metric = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="subcard p-3">
    <div className="eyebrow">{label}</div>
    <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{value}</div>
    {hint && <div className="text-xs text-glass-500 mt-0.5">{hint}</div>}
  </div>
);

/**
 * Popularity index and ranking position.
 *
 * The current index comes from the live rankings, which always work. Movement over time comes
 * from ARGUS's own snapshots, because the upstream per-server popularity timeseries route is
 * shadowed by a catch-all and cannot be queried. Until enough snapshots exist the card says so
 * rather than showing an empty chart.
 */
export const PopularitySummaryCard = memo(
  ({ current, currentRank, rankHistory, window }: PopularitySummaryCardProps) => {
    const stats = useMemo(() => {
      const points = (rankHistory?.data ?? []).map((point) => ({
        t: point.captured_at * 1000,
        v: point.popularity_index,
      }));
      return computeSeriesStats(points);
    }, [rankHistory]);

    const rankPoints = useMemo(
      () => (rankHistory?.data ?? []).filter((p) => p.rank_position !== null),
      [rankHistory],
    );

    const change =
      stats.latest !== null && stats.first !== null ? stats.latest - stats.first : null;
    const percentChange =
      change !== null && stats.first !== null && stats.first !== 0
        ? (change / stats.first) * 100
        : null;

    const TrendIcon = change === null || Math.abs(change) < 0.5 ? Minus : change > 0 ? TrendingUp : TrendingDown;
    const trendClass =
      change === null || Math.abs(change) < 0.5
        ? 'text-glass-400'
        : change > 0
          ? 'text-green-400'
          : 'text-red-400';

    const rankMovement =
      rankPoints.length >= 2
        ? (rankPoints[0].rank_position ?? 0) - (rankPoints[rankPoints.length - 1].rank_position ?? 0)
        : null;

    return (
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Popularity"
          icon={TrendingUp}
          description={`${POPULARITY_WINDOW_LABELS[window]} ranking window`}
        />

        <div className="grid grid-cols-3 gap-3 mt-3">
          {POPULARITY_WINDOWS.map((w) => (
            <div key={w} className={cn('subcard p-3', w === window && 'border-accent-400')}>
              <div className="eyebrow">{w}</div>
              <div className="text-lg font-semibold text-white tabular-nums mt-0.5">
                {formatPopularity(current[w])}
              </div>
              <div className="text-xs text-glass-500 mt-0.5 tabular-nums">
                {currentRank[w] !== undefined ? `Rank #${currentRank[w]}` : 'Unranked'}
              </div>
            </div>
          ))}
        </div>

        {rankHistory && stats.valueCount >= 2 ? (
          <>
            <div className="alert mt-3" role="status">
              <TrendIcon className={cn('w-4 h-4 shrink-0', trendClass)} />
              <div className="text-sm">
                <span className={trendClass}>
                  {change !== null && change >= 0 ? '+' : ''}
                  {formatPopularity(change)}
                </span>{' '}
                <span className="text-glass-400">
                  over this range
                  {percentChange !== null && ` (${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%)`}
                </span>
                {rankMovement !== null && rankMovement !== 0 && (
                  <span className="text-glass-400">
                    {' '}
                    · {rankMovement > 0 ? 'up' : 'down'} {Math.abs(rankMovement)}{' '}
                    {Math.abs(rankMovement) === 1 ? 'place' : 'places'}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Metric label="Peak" value={formatPopularity(stats.max)} />
              <Metric label="Average" value={formatPopularity(stats.average)} />
              <Metric label="Lowest" value={formatPopularity(stats.min)} />
              <Metric
                label="Volatility"
                value={`±${formatPopularity(stats.stdDev)}`}
                hint="Standard deviation"
              />
            </div>
          </>
        ) : (
          <p className="text-xs text-glass-500 mt-3">
            {rankHistory?.coverage.earliest
              ? `Only ${stats.valueCount} snapshots so far. ARGUS has been recording rankings since ${format(
                  new Date(rankHistory.coverage.earliest * 1000),
                  'd MMM yyyy',
                )}; a trend appears once there are a few hours of them.`
              : 'Popularity history is collected by ARGUS itself, because the upstream API cannot serve it. Trends appear once collection has been running for a few hours.'}
          </p>
        )}
      </div>
    );
  },
);

PopularitySummaryCard.displayName = 'PopularitySummaryCard';
